import {
  Badge,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useSession } from '../../app/session';
import { EmptyState } from '../../components/EmptyState';
import { formatCounter, formatDate } from '../../lib/format';
import { useAircraft } from './AircraftDetailLayout';

function Field({ label, value }: { label: string; value?: string | number | null }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <Text fz="xs" tt="uppercase" c="dimmed" fw={600}>
        {label}
      </Text>
      <Text fz="sm">{value}</Text>
    </div>
  );
}

export function AircraftOverviewTab() {
  const { t } = useTranslation();
  const aircraft = useAircraft();
  const { enumLabel } = useSession();
  const totals = aircraft.totals;

  return (
    <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
      <Card p="md">
        <Title order={3} mb="sm">
          {t('common.summary')}
        </Title>
        <SimpleGrid cols={2} spacing="sm">
          <Field label={t('aircraft.category')} value={enumLabel('aircraft_category', aircraft.category)} />
          <Field label={t('aircraft.class')} value={aircraft.aircraft_class} />
          <Field label={t('aircraft.manufacturer')} value={aircraft.manufacturer} />
          <Field label={t('aircraft.model')} value={`${aircraft.model} ${aircraft.variant}`.trim()} />
          <Field label={t('logbook.aircraftType')} value={aircraft.type_designator} />
          <Field label={t('aircraft.serialNumber')} value={aircraft.serial_number} />
          <Field label={t('aircraft.yearOfManufacture')} value={aircraft.year_of_manufacture} />
          <Field label={t('aircraft.seats')} value={aircraft.seats} />
          <Field label={t('aircraft.mtow')} value={aircraft.mtow_kg ?? undefined} />
          <Field label={t('aircraft.engineCount')} value={aircraft.engine_count} />
        </SimpleGrid>
        {(aircraft.launch_capabilities as string[] | undefined)?.length ? (
          <Stack gap={4} mt="md">
            <Text fz="xs" tt="uppercase" c="dimmed" fw={600}>
              {t('aircraft.launchCapabilities')}
            </Text>
            <Group gap={6}>
              {(aircraft.launch_capabilities as string[]).map((method) => (
                <Badge key={method} variant="light">
                  {enumLabel('launch_capability', method)}
                </Badge>
              ))}
            </Group>
          </Stack>
        ) : null}
        {aircraft.notes ? (
          <Text fz="sm" c="dimmed" mt="md">
            {aircraft.notes}
          </Text>
        ) : null}
      </Card>

      <Stack gap="md">
        <Card p="md">
          <Title order={3} mb="sm">
            {t('staff.totals')}
          </Title>
          {totals ? (
            <SimpleGrid cols={2} spacing="sm">
              <Field label={t('logbook.totalTime')} value={totals.total_display} />
              <Field label={t('logbook.title')} value={totals.flights} />
              <Field label={t('aircraft.landings')} value={totals.landings} />
              <Field label={t('aircraft.launches')} value={totals.launches} />
              <Field
                label={t('dashboard.flightsThisYear')}
                value={formatDate(totals.first_flight_date)}
              />
              <Field label={t('maintenance.lastDone')} value={formatDate(totals.last_flight_date)} />
            </SimpleGrid>
          ) : (
            <EmptyState title={t('dashboard.noFlightsYet')} />
          )}
        </Card>

        <Card p="md">
          <Title order={3} mb="sm">
            {t('aircraft.counters')}
          </Title>
          {aircraft.counters.length === 0 ? (
            <EmptyState title={t('aircraft.counters')} />
          ) : (
            <Table fz="sm">
              <Table.Tbody>
                {aircraft.counters.map((counter) => (
                  <Table.Tr key={counter.id}>
                    <Table.Td>{counter.label}</Table.Td>
                    <Table.Td ta="right" ff="monospace" fw={600}>
                      {formatCounter(counter.current_value, counter.unit)}
                    </Table.Td>
                    <Table.Td w={70}>
                      <Text fz="xs" c="dimmed">
                        {counter.unit === 'COUNT' ? t('common.count') : t('common.hours')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>

        <Card p="md">
          <Title order={3} mb="sm">
            {t('aircraft.maintainers')}
          </Title>
          {aircraft.maintainers.length === 0 ? (
            <EmptyState title={t('aircraft.maintainers')} />
          ) : (
            <Stack gap="xs">
              {aircraft.maintainers.map((maintainer) => (
                <Group key={maintainer.id} justify="space-between">
                  <Group gap="xs">
                    <Text fz="sm">{maintainer.person_name}</Text>
                    {maintainer.is_primary ? (
                      <Badge size="xs" color="teal">
                        {t('settings.globalEntry')}
                      </Badge>
                    ) : null}
                  </Group>
                  <Text fz="xs" c="dimmed">
                    {enumLabel('maintainer_role', maintainer.role)}
                    {maintainer.licence_reference ? ` · ${maintainer.licence_reference}` : ''}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
        </Card>

        {aircraft.components.length ? (
          <Card p="md">
            <Title order={3} mb="sm">
              {t('aircraft.components')}
            </Title>
            <Table fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('common.name')}</Table.Th>
                  <Table.Th>{t('aircraft.serialNumber')}</Table.Th>
                  <Table.Th>{t('maintenance.lastDone')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {aircraft.components.map((component) => (
                  <Table.Tr key={component.id}>
                    <Table.Td>
                      <Text fz="sm">{component.name}</Text>
                      <Text fz="xs" c="dimmed">
                        {enumLabel('component_kind', component.kind)}
                      </Text>
                    </Table.Td>
                    <Table.Td ff="monospace">{component.serial_number}</Table.Td>
                    <Table.Td>{formatDate(component.installed_on)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        ) : null}
      </Stack>
    </SimpleGrid>
  );
}
